import { useCallback } from 'react';

import {
  useGetNotifyExecutiveActionRequiredToolStateQuery,
  useUpdateNotifyExecutiveActionRequiredToolStateMutation,
} from '../services/notifyExecutiveActionRequiredTool.api';

export const NOTIFY_EXECUTIVE_ACTION_REQUIRED_RULE_NAME = 'NOTIFY_EXECUTIVE_ACTION_REQUIRED_TOOL_ENABLED';

export type SaveNotifyExecutiveToolRuleAction = 'created' | 'set-true' | 'set-false' | 'none';

interface UseNotifyExecutiveActionRequiredToolRuleResult {
  isToolEnabled: boolean;
  isRuleQueryLoading: boolean;
  isQueryError: boolean;
  queryError: unknown;
  isSaving: boolean;
  setToolEnabled: (next: boolean) => Promise<SaveNotifyExecutiveToolRuleAction>;
  refetch: () => Promise<unknown>;
}

const useNotifyExecutiveActionRequiredToolRule = (
  masterProjectEntityId: number | null,
  businessRuleDisplayName: string | null
): UseNotifyExecutiveActionRequiredToolRuleResult => {
  const skip = masterProjectEntityId === null || masterProjectEntityId <= 0;

  const {
    data,
    isLoading,
    isError,
    refetch,
    error: queryError,
  } = useGetNotifyExecutiveActionRequiredToolStateQuery(masterProjectEntityId!, { skip });

  const [updateToolState, { isLoading: isSaving }] = useUpdateNotifyExecutiveActionRequiredToolStateMutation();

  const setToolEnabled = useCallback(
    async (next: boolean): Promise<SaveNotifyExecutiveToolRuleAction> => {
      if (skip || masterProjectEntityId === null) {
        return 'none';
      }

      const response = await updateToolState({
        projectId: masterProjectEntityId,
        body: {
          enabled: next,
          displayName: businessRuleDisplayName?.trim() ? businessRuleDisplayName.trim() : null,
        },
      }).unwrap();

      await refetch();

      if (response.action === 'created') {
        return 'created';
      }

      return next ? 'set-true' : 'set-false';
    },
    [skip, masterProjectEntityId, businessRuleDisplayName, updateToolState, refetch]
  );

  return {
    isToolEnabled: data?.isToolEnabled ?? false,
    isRuleQueryLoading: isLoading,
    isQueryError: isError,
    queryError,
    isSaving,
    setToolEnabled,
    refetch,
  };
};

export default useNotifyExecutiveActionRequiredToolRule;
